import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Sparkles, User, Hand, Footprints, ArrowDownToLine, Heart, Send, Stethoscope } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Motion";
import { serviceCategories, laserEpilation, doctor } from "../lib/data";

const ZONE_ICONS = {
  intimate: Heart,
  legs: Footprints,
  arms: Hand,
  face: User,
  back: ArrowDownToLine,
};

export function ServicesIndex() {
  return (
    <div data-testid="page-services">
      <section className="pt-32 md:pt-40 pb-16">
        <div className="container-luxe">
          <Reveal>
            <div className="overline mb-6">Послуги</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight max-w-4xl">
              Преміальні напрямки
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-warmGray text-lg max-w-2xl leading-relaxed">
              Пʼять експертних напрямків клініки. Оберіть категорію, щоб переглянути
              детальний перелік процедур та цін.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-luxe">
          <Stagger className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {serviceCategories.map((cat) => (
              <StaggerItem key={cat.slug}>
                <Link
                  to={`/services/${cat.slug}`}
                  data-testid={`services-grid-${cat.slug}`}
                  className="group block relative overflow-hidden rounded-3xl bg-bone border border-line hover:border-nudeDark transition-all duration-500"
                >
                  <div className="relative h-80 overflow-hidden">
                    <img
                      loading="lazy"
                      decoding="async"
                      src={cat.cover}
                      onError={(e) => cat.fallback && (e.currentTarget.src = cat.fallback)}
                      alt={cat.title}
                      className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                    />
                  </div>
                  <div className="p-8">
                    <h3 className="font-serif text-3xl md:text-4xl tracking-tight">{cat.title}</h3>
                    <p className="mt-3 text-warmGray">{cat.subtitle}</p>
                    <div className="mt-7 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase">
                      Дивитись послуги <ArrowRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
}

function PriceRow({ name, price, oldPrice, coursePrice }) {
  return (
    <div className="flex items-baseline gap-4 py-4 border-b border-line">
      <div className="flex-1 text-ink text-sm md:text-base">
        {name}
        {coursePrice && (
          <div className="mt-1 text-[11px] tracking-[0.18em] uppercase text-warmGray">
            Курс 10 процедур · {coursePrice}
          </div>
        )}
      </div>
      <div className="flex items-baseline gap-3 shrink-0 text-right">
        {oldPrice && (
          <div className="text-warmGray/70 text-xs line-through">{oldPrice}</div>
        )}
        {price && (
          <div className="font-serif text-lg md:text-xl tracking-tight text-ink whitespace-nowrap">
            {price}
          </div>
        )}
      </div>
    </div>
  );
}

function SimpleItem({ name }) {
  return (
    <div className="flex items-start gap-4 py-4 border-b border-line">
      <Check size={18} strokeWidth={1.2} className="text-warmGray mt-1 shrink-0" />
      <div className="text-ink">{name}</div>
    </div>
  );
}

function CategorySection({ section }) {
  return (
    <div className="mb-14 last:mb-0">
      <div className="overline mb-5 text-warmGray">{section.title}</div>
      <div className="grid md:grid-cols-2 gap-x-12 gap-y-0">
        {section.items.map((it, i) => (
          <PriceRow key={i} name={it.name} price={it.price} oldPrice={it.oldPrice} coursePrice={it.coursePrice} />
        ))}
      </div>
    </div>
  );
}

function WeightProgramBlock({ data }) {
  const mp = data.medicalProgram || {};
  return (
    <div data-testid="weight-program-block" className="space-y-10">
      <Reveal>
        <div className="rounded-3xl bg-sand/50 border border-line p-8 md:p-12">
          <div className="overline text-warmGray mb-4">Програма</div>
          <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.05] max-w-2xl">
            {data.lead}
          </h3>
        </div>
      </Reveal>

      <div className="grid lg:grid-cols-12 gap-8">
        <Reveal className="lg:col-span-7">
          <div className="rounded-3xl border border-line bg-bone p-8 md:p-10 h-full">
            <div className="overline text-warmGray mb-3">{data.includesTitle}</div>
            <Stagger className="space-y-0 mt-4">
              {data.includes.map((it) => (
                <StaggerItem key={it}>
                  <div className="flex items-start gap-4 py-4 border-b border-line last:border-b-0">
                    <Check size={18} strokeWidth={1.3} className="text-ink mt-1 shrink-0" />
                    <div className="text-ink text-sm md:text-base">{it}</div>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-5">
          <div className="rounded-3xl bg-ink text-bone p-8 md:p-10 h-full flex flex-col justify-between">
            <div>
              <div className="overline text-bone/50 mb-3">Старт програми</div>
              <p className="text-bone/70 text-sm leading-relaxed">{data.programPriceLabel}</p>
              <div className="font-serif text-5xl md:text-6xl tracking-tight mt-6">
                {data.programPrice}
              </div>
            </div>
            <a
              href={doctor.telegram}
              target="_blank"
              rel="noreferrer"
              data-testid="weight-program-cta"
              className="mt-10 inline-flex items-center justify-center gap-3 rounded-full bg-bone text-ink px-7 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-nude transition self-start"
            >
              <Send size={14} strokeWidth={1.5} /> Записатися на програму
            </a>
          </div>
        </Reveal>
      </div>

      {mp.title && (
        <Reveal>
          <div className="rounded-3xl border border-line bg-bone p-8 md:p-12">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-8">
                <div className="overline text-warmGray mb-3">Індивідуальний підхід</div>
                <h4 className="font-serif text-3xl md:text-4xl tracking-tight leading-[1.1]">
                  {mp.title}
                </h4>
                <p className="mt-6 text-ink text-base md:text-lg leading-relaxed max-w-2xl">
                  {mp.description}
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <a
                  href={doctor.telegram}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="weight-consult-cta"
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-ink text-bone px-8 py-5 text-[11px] tracking-[0.28em] uppercase hover:bg-black transition w-full lg:w-auto"
                >
                  <Stethoscope size={14} strokeWidth={1.5} /> {mp.ctaLabel || "Записатися на консультацію"}
                </a>
                <p className="mt-4 text-[11px] text-warmGray lg:text-right">
                  Telegram {doctor.telegramHandle}
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      )}
    </div>
  );
}

function LaserEpilationBlock() {
  const data = laserEpilation;
  return (
    <section data-testid="laser-epilation-block" className="mt-20 md:mt-28 pt-16 md:pt-20 border-t border-line">
      <Reveal>
        <div className="text-center mb-14">
          <div className="overline text-warmGray mb-4">{data.brandLine}</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Лазерна епіляція
          </h2>
          <p className="mt-4 text-warmGray text-sm md:text-base tracking-wide">{data.tagline}</p>
        </div>
      </Reveal>

      {/* Zone pricing */}
      <Stagger className="space-y-14">
        {data.zones.map((zone) => {
          const Icon = ZONE_ICONS[zone.icon] || Sparkles;
          return (
            <StaggerItem key={zone.title}>
              <div data-testid={`zone-${zone.icon}`} className="rounded-3xl bg-sand/40 border border-line p-6 md:p-10">
                <div className="flex items-center gap-4 mb-6 pb-5 border-b border-line">
                  <div className="w-12 h-12 rounded-full bg-bone border border-line flex items-center justify-center">
                    <Icon size={20} strokeWidth={1.3} />
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl tracking-tight">{zone.title}</h3>
                </div>
                <div className="hidden md:grid grid-cols-12 pb-3 text-[10px] tracking-[0.25em] uppercase text-warmGray">
                  <div className="col-span-6">Послуга</div>
                  <div className="col-span-3 text-right">Жінки</div>
                  <div className="col-span-3 text-right">Чоловіки</div>
                </div>
                <div className="space-y-0">
                  {zone.items.map((it, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-12 gap-2 py-4 border-b border-line last:border-b-0 items-baseline"
                    >
                      <div className="col-span-12 md:col-span-6 text-ink text-sm md:text-base">{it.name}</div>
                      <div className="col-span-6 md:col-span-3 md:text-right">
                        <span className="md:hidden text-[10px] tracking-[0.25em] uppercase text-warmGray mr-2">Жінки</span>
                        <span className="font-serif text-base md:text-lg">{it.female || "—"}</span>
                      </div>
                      <div className="col-span-6 md:col-span-3 md:text-right">
                        <span className="md:hidden text-[10px] tracking-[0.25em] uppercase text-warmGray mr-2">Чоловіки</span>
                        <span className="font-serif text-base md:text-lg">{it.male || "—"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>

      {/* Promo packages */}
      <Reveal className="mt-14">
        <div className="rounded-3xl bg-ink text-bone p-8 md:p-12">
          <div className="overline text-bone/50 mb-3">Акційні комплекси</div>
          <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-8">Спеціальні пропозиції</h3>
          <div className="space-y-0">
            {data.promoPackages.map((p, i) => (
              <div
                key={i}
                className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-2 py-5 border-b border-bone/10 last:border-b-0"
              >
                <div className="text-bone text-sm md:text-base">{p.name}</div>
                <div className="flex items-baseline gap-4">
                  <div className="text-bone/40 text-sm line-through">{p.oldPrice}</div>
                  <div className="font-serif text-xl md:text-2xl text-bone">{p.price}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-bone/15">
            <div className="overline text-bone/50 mb-4">Все тіло</div>
            <div className="grid md:grid-cols-2 gap-4">
              {data.fullBody.map((b, i) => (
                <div
                  key={i}
                  className="flex items-baseline justify-between py-4 px-5 rounded-2xl bg-bone/[0.04] border border-bone/10"
                >
                  <div className="text-bone text-sm md:text-base">{b.name}</div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-bone/40 text-xs line-through">{b.oldPrice}</span>
                    <span className="font-serif text-xl text-bone">{b.price}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      {/* Benefits */}
      <Reveal className="mt-14">
        <div className="rounded-3xl border border-line bg-bone p-8 md:p-12">
          <div className="overline mb-3 text-warmGray">Переваги</div>
          <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-8">
            Чому обирають нашу лазерну епіляцію
          </h3>
          <ul className="grid md:grid-cols-2 gap-x-10 gap-y-0">
            {data.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 py-3 border-b border-line last:border-b-0 md:[&:nth-last-child(2)]:border-b-0">
                <Check size={18} strokeWidth={1.3} className="mt-1 shrink-0 text-ink" />
                <span className="text-ink text-sm md:text-base">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>

      {/* Slogan */}
      <Reveal className="mt-16 text-center">
        <p className="font-serif italic text-2xl md:text-4xl tracking-tight text-ink">
          “{data.slogan}”
        </p>
      </Reveal>
    </section>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const cat = serviceCategories.find((c) => c.slug === slug);
  if (!cat) return <Navigate to="/services" replace />;
  const idx = serviceCategories.findIndex((c) => c.slug === slug);
  const next = serviceCategories[(idx + 1) % serviceCategories.length];

  return (
    <div data-testid={`page-service-${slug}`}>
      <section className="pt-28 md:pt-32 pb-12">
        <div className="container-luxe">
          <Link to="/services" className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-warmGray hover:text-ink" data-testid="back-to-services">
            <ArrowLeft size={14} strokeWidth={1.5} /> Усі послуги
          </Link>
        </div>
      </section>

      <section className="pb-16">
        <div className="container-luxe grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="overline mb-5">Категорія</div>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-serif text-5xl md:text-7xl leading-[0.96] tracking-tight">
                {cat.title}
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-warmGray text-lg max-w-xl">{cat.subtitle}</p>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-5">
            <img
              loading="lazy"
              decoding="async"
              src={cat.cover}
              onError={(e) => cat.fallback && (e.currentTarget.src = cat.fallback)}
              alt={cat.title}
              className="w-full h-[420px] md:h-[520px] object-cover rounded-3xl"
            />
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-luxe">
          {cat.weightProgram && (
            <div className="mb-16">
              <WeightProgramBlock data={cat.weightProgram} />
            </div>
          )}

          {(Array.isArray(cat.items) && cat.items.length > 0) || (Array.isArray(cat.sections) && cat.sections.length > 0) ? (
            <Reveal>
              <div className="overline mb-3">Процедури</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight max-w-2xl">
                {cat.weightProgram ? "Напрямки супроводу" : "Перелік процедур"}
              </h2>
            </Reveal>
          ) : null}

          <div className="mt-10 max-w-5xl">
            {Array.isArray(cat.items) && cat.items.length > 0 && (
              <Stagger className="grid md:grid-cols-2 gap-x-12 gap-y-0">
                {cat.items.map((it) => (
                  <StaggerItem key={typeof it === "string" ? it : it.name}>
                    {typeof it === "string" ? (
                      <SimpleItem name={it} />
                    ) : (
                      <PriceRow name={it.name} price={it.price} oldPrice={it.oldPrice} coursePrice={it.coursePrice} />
                    )}
                  </StaggerItem>
                ))}
              </Stagger>
            )}

            {Array.isArray(cat.sections) && cat.sections.map((s) => (
              <div key={s.title} className="mt-14 first:mt-12">
                <Reveal>
                  <CategorySection section={s} />
                </Reveal>
              </div>
            ))}
          </div>

          {cat.hasLaserEpilation && <LaserEpilationBlock />}

          <Reveal>
            <div className="mt-16 flex flex-col sm:flex-row gap-3">
              <Link
                to="/booking"
                state={{ procedure: cat.title }}
                data-testid="service-book-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-ink text-bone px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-black transition"
              >
                Записатися <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              <Link
                to="/consultations"
                data-testid="service-consult-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-ink hover:text-bone transition"
              >
                Запис на консультацію
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-luxe">
          <Link
            to={`/services/${next.slug}`}
            data-testid="service-next-cta"
            className="group block relative overflow-hidden rounded-3xl border border-line p-8 md:p-12 hover:bg-sand/60 transition"
          >
            <div className="overline mb-3 text-warmGray">Наступний напрямок</div>
            <div className="flex items-center justify-between gap-6">
              <div className="font-serif text-3xl md:text-5xl tracking-tight">{next.title}</div>
              <ArrowRight size={32} strokeWidth={1} className="shrink-0 transition-transform group-hover:translate-x-2" />
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
